import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const ROTATE_INTERVAL = 10000; // 10 seconds for images

/**
 * Slideshow component for images and videos.
 * @param {Array} items - Array of { type: 'image'|'video', src: string }
 */
const Slideshow = ({ items }) => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!items || items.length === 0) return;
    // Clear any previous timer
    if (timerRef.current) clearTimeout(timerRef.current);

    if (items[index].type === 'image') {
      // For images, advance after ROTATE_INTERVAL
      timerRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % items.length);
      }, ROTATE_INTERVAL);
    }
    // For videos, advance on onEnded
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [index, items]);

  const handleVideoEnd = () => {
    setIndex((prev) => (prev + 1) % items.length);
  };

  if (!items || items.length === 0) {
    return <div className="flex items-center justify-center w-full h-full text-green-400">No slideshow content.</div>;
  }

  return (
    <div
      className="flex items-center justify-center bg-black border border-green-700 rounded-lg shadow-lg mx-auto"
      style={{
        width: '100%',
        maxWidth: '960px',
        aspectRatio: '16 / 9',
        background: '#111',
        overflow: 'hidden',
      }}
    >
      {items[index].type === 'image' ? (
        <img
          src={items[index].src}
          alt="slideshow"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <video
          src={items[index].src}
          autoPlay
          muted
          playsInline
          controls={false}
          onEnded={handleVideoEnd}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      )}
    </div>
  );
};

Slideshow.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['image', 'video']).isRequired,
      src: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default Slideshow; 